package com.vizio.redwolf.dms.model

import kotlinx.serialization.ExperimentalSerializationApi
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.js.JsExport

@Serializable
@JsExport
sealed class PayNonceStatusFieldedResponse {
    @Serializable
    class Invalid(
        @SerialName("message") val message: String? = null,
        @SerialName("responseCode") val responseCode: String? = null
    ) : PayNonceStatusFieldedResponse()

    @Serializable
    @SerialName("device_pending")
    class Pending(
        @SerialName("id")
        val pin: String,
        val deviceId: String,
        val deviceInstanceId: String,
        val serialNumber: String,
        val friendlyName: String,
        val skipCreditCard: Boolean,
        val userId: String? = null,
        val deepLinkValue: String? = null,
        val expiresAt: String? = null,
        val launchSource: String? = null,
        val xVizTraceId: String? = null
    ) : PayNonceStatusFieldedResponse() {
        val responseCode: String by lazy {
            @OptIn(ExperimentalSerializationApi::class)
            serializer().descriptor.serialName
        }
    }

    @Serializable
    @SerialName("device_registered")
    class Registered(
        @SerialName("id")
        val pin: String,
        val deviceId: String,
        val deviceInstanceId: String,
        val serialNumber: String,
        val friendlyName: String,
        val skipCreditCard: Boolean,
        val userId: String? = null,
        val deepLinkValue: String? = null,
        val expiresAt: String? = null,
        val launchSource: String? = null,
        val xVizTraceId: String? = null
    ) : PayNonceStatusFieldedResponse() {
        val responseCode: String by lazy {
            @OptIn(ExperimentalSerializationApi::class)
            serializer().descriptor.serialName
        }
    }

    @Serializable
    @SerialName("device_pin_expired")
    class PinExpired(
        val message: String,
    ) : PayNonceStatusFieldedResponse() {
        val responseCode: String by lazy {
            @OptIn(ExperimentalSerializationApi::class)
            serializer().descriptor.serialName
        }
    }

    @Serializable
    @SerialName("device_does_not_exist")
    class NotExist(
        val message: String,
    ) : PayNonceStatusFieldedResponse() {
        val responseCode: String by lazy {
            @OptIn(ExperimentalSerializationApi::class)
            serializer().descriptor.serialName
        }
    }

    class Failed(val message: String) : PayNonceStatusFieldedResponse()
    class Error(val exception: Throwable) : PayNonceStatusFieldedResponse()
    class Timeout(val exception: Throwable) : PayNonceStatusFieldedResponse()

    fun <R> fold(
        onInvalid: (invalid: Invalid) -> R,
        onPending: (pending: Pending) -> R,
        onRegistered: (registered: Registered) -> R,
        onNotExist: (notExist: NotExist) -> R,
        onPinExpired: (pinExpired: PinExpired) -> R,
        onFailed: (failed: Failed) -> R,
        onError: (error: Error) -> R,
        onTimeout: (timeout: Timeout) -> R
    ): R {
        return when (this) {
            is Invalid -> onInvalid(Invalid(this.message, this.responseCode))
            is Pending -> onPending(Pending(this.pin, this.deviceId, this.deviceInstanceId, this.serialNumber, this.friendlyName, this.skipCreditCard))
            is Registered -> onRegistered(Registered(this.pin, this.deviceId, this.deviceInstanceId, this.serialNumber, this.friendlyName, this.skipCreditCard))
            is NotExist -> onNotExist(NotExist(this.message))
            is PinExpired -> onPinExpired(PinExpired(this.message))
            is Failed -> onFailed(Failed(this.message))
            is Error -> onError(Error(this.exception))
            is Timeout -> onTimeout(Timeout(this.exception))
        }
    }
}